import React, { useEffect, useState } from "react";
import ApexCharts from "apexcharts";
import jsonData from "../../../../current-survey-numeric.json";
import { FILTER_CONFIG } from "../../../../config/filterconfig"; // Import filter config
import "../../../../pages/D04_Responses_by_Demographic_Category.css";

const StackedBarChart = ({
  selectedQuestion,
  selectedDemographic,
  filterCriteria,
}) => {
  const [chartData, setChartData] = useState({
    favorable: [],
    neutral: [],
    unfavorable: [],
    noResponse: [],
  });
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    // Find the config for the selected demographic field
    const demographicConfig = FILTER_CONFIG.find(
      (item) => item.fieldName === selectedDemographic || item.jsonKey === selectedDemographic
    );

    // Handle the case where config isn't found or is invalid
    if (!demographicConfig) {
      console.error(`No config found for demographic: ${selectedDemographic}`);
      setCategories([]);
      setChartData({
        favorable: [],
        neutral: [],
        unfavorable: [],
        noResponse: [],
      });
      return;
    }

    // Get the correct field to use from config
    const fieldToUse = demographicConfig.jsonKey || selectedDemographic;
    
    // Safely get the predefined sort order
    const predefinedSortOrder = demographicConfig.sortOrder || [];

    // Helper function to get sorted field values
    const getSortedFieldValues = (fieldName) => {
      // Ensure the field exists in the data
      if (!jsonData[0] || typeof jsonData[0][fieldName] === 'undefined') {
        console.error(`Field ${fieldName} not found in the data`);
        return [];
      }

      let values = [...new Set(jsonData.map((item) => item[fieldName]))].filter(
        Boolean
      );

      if (predefinedSortOrder && predefinedSortOrder.length > 0) {
        values = values.sort((a, b) => {
          const indexA = predefinedSortOrder.indexOf(a);
          const indexB = predefinedSortOrder.indexOf(b);

          if (indexA === -1 && indexB === -1) return a.localeCompare(b); // Alphabetical only if both are missing
          if (indexA === -1) return 1; // Push missing values to the end
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
      } else {
        values = values.sort((a, b) => a.localeCompare(b));
      }
      return values;
    };

    const demographicValues = getSortedFieldValues(fieldToUse);

    if (!demographicValues || demographicValues.length === 0) {
      setCategories([]);
      setChartData({
        noResponse: [],
        unfavorable: [],
        neutral: [],
        favorable: [],
      });
      return;
    }

    // Calculate total responses across all demographics
    const globalTotals = {
      favorable: 0,
      neutral: 0,
      unfavorable: 0,
      noResponse: 0,
    };

    jsonData.forEach((response) => {
      const answer = response[selectedQuestion];
      if (answer === 4 || answer === 5) {
        globalTotals.favorable++;
      } else if (answer === 3) {
        globalTotals.neutral++;
      } else if (answer === 1 || answer === 2) {
        globalTotals.unfavorable++;
      } else {
        globalTotals.noResponse++;
      }
    });

    const totalResponses =
      Object.values(globalTotals).reduce((sum, val) => sum + val, 0) || 1; // Avoid division by zero

    const data = demographicValues.reduce(
      (acc, value) => {
        const filteredData = filterDataByCriteria(
          jsonData,
          fieldToUse,
          value,
          filterCriteria
        );

        let favorable = 0;
        let neutral = 0;
        let unfavorable = 0;
        let noResponse = 0;

        filteredData.forEach((response) => {
          const answer = response[selectedQuestion];
          if (answer === 4 || answer === 5) {
            favorable++;
          } else if (answer === 3) {
            neutral++;
          } else if (answer === 1 || answer === 2) {
            unfavorable++;
          } else {
            noResponse++;
          }
        });

        // Convert counts to percentages relative to the global total
        acc.noResponse.push((noResponse / totalResponses) * 100);
        acc.unfavorable.push((unfavorable / totalResponses) * 100);
        acc.neutral.push((neutral / totalResponses) * 100);
        acc.favorable.push((favorable / totalResponses) * 100);

        return acc;
      },
      { noResponse: [], unfavorable: [], neutral: [], favorable: [] }
    );

    setChartData(data);
    setCategories(demographicValues);
  }, [selectedQuestion, selectedDemographic, filterCriteria]);

  useEffect(() => {
    // Only render the chart if we have categories
    if (!categories || categories.length === 0) return;

    const barHeight = 20;
    const chartHeight = Math.max(300, categories.length * barHeight + 100); // Min height

    const stackedBarsOptions = {
      series: [
        { name: "No Response", data: chartData.noResponse },
        { name: "Unfavorable", data: chartData.unfavorable },
        { name: "Neutral", data: chartData.neutral },
        { name: "Favorable", data: chartData.favorable },
      ],
      chart: {
        type: "bar",
        height: chartHeight,
        stacked: true,
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "20px",
        },
      },
      xaxis: {
        categories: categories, // Ensure sorting is applied to x-axis
        title: {
          text: "Percentage of Total Responses",
        },
      },
      tooltip: {
        y: {
          formatter: (val) => `${val.toFixed(2)}%`, // Show two decimals in tooltips
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(2)}%`, // Show percentages on the bars
        style: {
          fontSize: "12px",
          colors: ["#fff"], // Adjust as needed
        },
      },
      colors: ["#bab0ac", "#f28e2b", "#f1ce63", "#499894"],
    };

    // Make sure the chart element exists
    const chartElement = document.querySelector("#stacked-bar-chart");
    if (!chartElement) {
      console.error('Chart element not found in DOM');
      return;
    }

    const chart = new ApexCharts(chartElement, stackedBarsOptions);
    chart.render();

    return () => {
      // Safely destroy chart
      if (chart) chart.destroy();
    };
  }, [chartData, categories]);

  const filterDataByCriteria = (
    data,
    demographicType,
    demographicValue,
    criteria
  ) => {
    return data.filter((item) => {
      if (!item[demographicType] || item[demographicType] === null)
        return false; // Skip null values
      let match = true;
      Object.keys(criteria).forEach((key) => {
        if (
          criteria[key] && // Check criteria exists
          Array.isArray(criteria[key]) && // Check it's an array
          criteria[key].length > 0 && // Has values
          (!item[key] || !criteria[key].includes(item[key]))
        ) {
          match = false;
        }
      });
      return match && item[demographicType] === demographicValue;
    });
  };

  return (
    <div className="D04_Responses_by_Demographic_Category-internal-charts">
      <div className="D04_Responses_by_Demographic_Category-internal-chart-container">
        <div
          id="stacked-bar-chart"
          className="D04_Responses_by_Demographic_Category-internal-chart"
        ></div>
      </div>
    </div>
  );
};

export default StackedBarChart;